import { pr, theme, ui } from "aaa";
import { ListItem, ListItemWithJsxSymbol } from "components/common/LandingPage";
import { NativeIconButton } from "components/common/NativeIconButton";
import { NativeModal } from "components/common/NativeModal";
import { NativeTooltip } from "components/common/NativeTooltip";
import { FormattedHTMLMessage } from "i18n/util";
import * as React from "react";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import { InfoContainer } from "./HouseholdInsuranceInfoContainer";
import { SectionContainer } from "./HouseholdInsuranceSectionContainer";

const upgradeOptionIconColor = theme.themeColors.grey_300;
/** Sets `color` for descendants; SVGs that use `currentColor` / `fill: currentcolor` pick this up. */
const upgradeOptionIconWrapperStyle: React.CSSProperties = {
    alignItems: "center",
    color: upgradeOptionIconColor,
    display: "inline-flex",
    lineHeight: 0,
};

const Title = ({ children }: { children: React.ReactNode }) => (
    <pr.P_16_TITLE
        style={{
            fontWeight: "bold",
        }}
    >
        {children}
    </pr.P_16_TITLE>
);

const InfoIconWithTooltip = ({
    title,
    tooltip,
}: {
    title: string;
    tooltip: string;
}) => {
    const tooltipRef = React.useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = React.useState(false);
    const isMobile = externalInterfaceStore.isGeorgeApp || ui.windowWidth < 600;

    const handleMouseOver = () => {
        ui.isPopupVisible = true;
        setIsOpen(true);
    };

    const handleMouseOut = () => {
        ui.isPopupVisible = false;
        setIsOpen(false);
    };

    const handleClick = (event: React.MouseEvent) => {
        console.log("Icon clicked");
        event.stopPropagation();
        console.log("Icon clicked");
        ui.isPopupVisible = true;
        setIsOpen(true);
    };

    const handleCloseModal = () => {
        ui.isPopupVisible = false;
        setIsOpen(false);
    };

    return (
        <>
            {isMobile ? (
                <NativeIconButton
                    aria-label={ui.__("common.information")}
                    disabled={!isMobile}
                    focusStyle={{
                        borderRadius: 4,
                        height: 18,
                        width: 18,
                    }}
                    style={{
                        padding: 0,
                        height: 18,
                        width: 18,
                    }}
                    onClickWithEvent={handleClick}
                >
                    <pr.IconInfoCircleHollow
                        style={{
                            color: theme.themeColors.ui_blue,
                            height: 18,
                            width: 18,
                        }}
                    />
                </NativeIconButton>
            ) : (
                <div
                    aria-label={ui.__("common.information")}
                    ref={tooltipRef}
                    role={isMobile ? "button" : undefined}
                    style={{
                        cursor: "help",
                    }}
                    onMouseOver={handleMouseOver}
                    onMouseOut={handleMouseOut}
                >
                    <pr.IconInfoCircleHollow
                        style={{
                            color: theme.themeColors.ui_blue,
                            height: 18,
                            width: 18,
                        }}
                    />
                </div>
            )}
            {!isMobile ? (
                <NativeTooltip
                    anchorEl={tooltipRef.current}
                    content={tooltip}
                    open={isOpen}
                    placement="right"
                    style={{
                        maxWidth: 320,
                    }}
                />
            ) : (
                <NativeModal
                    open={isOpen}
                    paperStyle={{ borderRadius: theme.BORDER_RADIUS }}
                    bodyStyle={{ padding: 16 }}
                    contentStyle={{
                        height: 400,
                        maxWidth: theme.MAX_APP_WIDTH,
                        // Override the default transform to slide up from the bottom
                        // per George explicit request.
                        transform: isOpen
                            ? "translate(0, 64px)"
                            : "translate(0, 100vh)",
                        width: "95%",
                    }}
                >
                    <div
                        style={{
                            alignItems: "start",
                            display: "flex",
                            height: "100",
                            justifyContent: "space-between",
                            width: "100%",
                        }}
                    >
                        <div
                            style={{
                                alignItems: "start",
                                display: "flex",
                                gap: 8,
                                height: "100",
                                width: "100%",
                            }}
                        >
                            <pr.IconInfoCircleHollow
                                style={{
                                    color: theme.customColors.georgeLightGrey,
                                    fontSize: 18,
                                    flexShrink: 0,
                                }}
                            />
                            <div>
                                <pr.P_18_TITLE>{title}</pr.P_18_TITLE>
                                <pr.P_14_CONTENT
                                    style={{ lineHeight: "20px", marginTop: 4 }}
                                >
                                    {tooltip}
                                </pr.P_14_CONTENT>
                            </div>
                        </div>
                        <NativeIconButton
                            aria-label={ui.__("common.close")}
                            onClick={handleCloseModal}
                            focusStyle={{ borderRadius: 4 }}
                            style={{
                                height: 24,
                                width: 24,
                            }}
                        >
                            <pr.IconClose
                                style={{
                                    color: theme.customColors.georgeLightGrey,
                                }}
                            />
                        </NativeIconButton>
                    </div>
                </NativeModal>
            )}
        </>
    );
};

const ListItemWithTooltip = ({
    maxWidth,
    symbol,
    text,
    tooltip,
}: {
    maxWidth?: number;
    symbol: React.ReactElement;
    text: string;
    tooltip?: string;
}) => {
    const isMobile = externalInterfaceStore.isGeorgeApp || ui.windowWidth < 600;

    return (
        <ListItemWithJsxSymbol
            symbol={<span style={upgradeOptionIconWrapperStyle}>{symbol}</span>}
        >
            <pr.HFlex
                style={{
                    alignItems: "center",
                    gap: 12,
                }}
            >
                <pr.P_16_CONTENT
                    style={{
                        maxWidth: isMobile && maxWidth ? maxWidth : undefined,
                    }}
                >
                    {text}
                </pr.P_16_CONTENT>
                {tooltip && (
                    <InfoIconWithTooltip title={text} tooltip={tooltip} />
                )}
            </pr.HFlex>
        </ListItemWithJsxSymbol>
    );
};

export const HouseholdInsuranceBenefitsInfo = () => {
    return (
        <>
            <SectionContainer>
                <InfoContainer>
                    {/* <Title
                    >
                        {ui.__("householdInsurance.landingPage.benefitsInfo.householdInsurance.title")}
                    </Title> */}
                    <pr.P_16_CONTENT
                        style={{
                            lineHeight: "24px",
                        }}
                    >
                        <FormattedHTMLMessage id="householdInsurance.landingPage.benefitsInfo.householdInsurance.description" />
                    </pr.P_16_CONTENT>
                    <ListItem>
                        <pr.P_16_CONTENT>
                            {ui.__(
                                "householdInsurance.landingPage.benefitsInfo.householdInsurance.listItem1",
                            )}
                        </pr.P_16_CONTENT>
                    </ListItem>
                    <ListItem>
                        <pr.P_16_CONTENT>
                            {ui.__(
                                "householdInsurance.landingPage.benefitsInfo.householdInsurance.listItem2",
                            )}
                        </pr.P_16_CONTENT>
                    </ListItem>
                    <ListItem>
                        <pr.P_16_CONTENT>
                            {ui.__(
                                "householdInsurance.landingPage.benefitsInfo.householdInsurance.listItem3",
                            )}
                        </pr.P_16_CONTENT>
                    </ListItem>
                    <ListItem>
                        <pr.P_16_CONTENT>
                            {ui.__(
                                "householdInsurance.landingPage.benefitsInfo.householdInsurance.listItem4",
                            )}
                        </pr.P_16_CONTENT>
                    </ListItem>
                    <ListItem>
                        <pr.P_16_CONTENT>
                            {ui.__(
                                "householdInsurance.landingPage.benefitsInfo.householdInsurance.listItem5",
                            )}
                        </pr.P_16_CONTENT>
                    </ListItem>
                    <ListItem>
                        <pr.P_16_CONTENT>
                            {ui.__(
                                "householdInsurance.landingPage.benefitsInfo.householdInsurance.listItem6",
                            )}
                        </pr.P_16_CONTENT>
                    </ListItem>
                    <ListItem>
                        <pr.P_16_CONTENT>
                            {ui.__(
                                "householdInsurance.landingPage.benefitsInfo.householdInsurance.listItem7",
                            )}
                        </pr.P_16_CONTENT>
                    </ListItem>
                    <ListItem>
                        <pr.P_16_CONTENT>
                            {ui.__(
                                "householdInsurance.landingPage.benefitsInfo.householdInsurance.listItem8",
                            )}
                        </pr.P_16_CONTENT>
                    </ListItem>
                </InfoContainer>
                <InfoContainer>
                    <Title>
                        {ui.__(
                            "householdInsurance.landingPage.benefitsInfo.coveredRisks.title",
                        )}
                    </Title>
                    <ListItem>
                        <pr.P_16_CONTENT>
                            {ui.__(
                                "householdInsurance.landingPage.benefitsInfo.coveredRisks.listItem1",
                            )}
                        </pr.P_16_CONTENT>
                    </ListItem>
                    <ListItem>
                        <pr.P_16_CONTENT>
                            {ui.__(
                                "householdInsurance.landingPage.benefitsInfo.coveredRisks.listItem2",
                            )}
                        </pr.P_16_CONTENT>
                    </ListItem>
                    <ListItem>
                        <pr.P_16_CONTENT>
                            {ui.__(
                                "householdInsurance.landingPage.benefitsInfo.coveredRisks.listItem3",
                            )}
                        </pr.P_16_CONTENT>
                    </ListItem>
                    <ListItem>
                        <pr.P_16_CONTENT>
                            {ui.__(
                                "householdInsurance.landingPage.benefitsInfo.coveredRisks.listItem4",
                            )}
                        </pr.P_16_CONTENT>
                    </ListItem>
                </InfoContainer>
                <InfoContainer>
                    <Title>
                        {ui.__(
                            "householdInsurance.landingPage.benefitsInfo.privatLiability.title",
                        )}
                    </Title>
                    <ListItem>
                        <pr.P_16_CONTENT>
                            {ui.__(
                                "householdInsurance.landingPage.benefitsInfo.privatLiability.listItem1",
                            )}
                        </pr.P_16_CONTENT>
                    </ListItem>
                    <ListItem>
                        <pr.P_16_CONTENT>
                            {ui.__(
                                "householdInsurance.landingPage.benefitsInfo.privatLiability.listItem2",
                            )}
                        </pr.P_16_CONTENT>
                    </ListItem>
                </InfoContainer>
            </SectionContainer>
            <pr.LightSeparatorLine spacing={40} />
            <SectionContainer>
                <InfoContainer>
                    <Title>
                        {ui.__(
                            "householdInsurance.landingPage.benefitsInfo.upgrade.title",
                        )}
                    </Title>
                    <pr.P_16_CONTENT>
                        {ui.__(
                            "householdInsurance.landingPage.benefitsInfo.upgrade.description",
                        )}
                    </pr.P_16_CONTENT>
                </InfoContainer>
                <InfoContainer>
                    <Title>
                        {ui.__(
                            "householdInsurance.landingPage.benefitsInfo.upgrade.options.title",
                        )}
                    </Title>
                    <ListItemWithTooltip
                        symbol={<pr.IconBrokenGlass />}
                        text={ui.__(
                            "householdInsurance.landingPage.benefitsInfo.upgrade.options.brokenGlass",
                        )}
                        tooltip={ui.__(
                            "householdInsurance.landingPage.benefitsInfo.upgrade.options.brokenGlass.tooltip",
                        )}
                    />
                    <ListItemWithTooltip
                        symbol={<pr.IconHomeSecondary />}
                        text={ui.__(
                            "householdInsurance.landingPage.benefitsInfo.upgrade.options.heatingHomeTech",
                        )}
                        tooltip={ui.__(
                            "householdInsurance.landingPage.benefitsInfo.upgrade.options.heatingHomeTech.tooltip",
                        )}
                    />
                    <ListItemWithTooltip
                        symbol={<pr.IconHolidays />}
                        text={ui.__(
                            "householdInsurance.landingPage.benefitsInfo.upgrade.options.outdoorPool",
                        )}
                        tooltip={ui.__(
                            "householdInsurance.landingPage.benefitsInfo.upgrade.options.outdoorPool.tooltip",
                        )}
                    />
                    <ListItemWithTooltip
                        symbol={<pr.IconExtendedPremiumRisks />}
                        text={ui.__(
                            "householdInsurance.landingPage.benefitsInfo.upgrade.options.extendedPremiumRisks",
                        )}
                        tooltip={ui.__(
                            "householdInsurance.landingPage.benefitsInfo.upgrade.options.extendedPremiumRisks.tooltip",
                        )}
                    />
                    <ListItemWithTooltip
                        symbol={<pr.IconWrench />}
                        text={ui.__(
                            "householdInsurance.landingPage.benefitsInfo.upgrade.options.emergencyHomeAssistance",
                        )}
                        tooltip={ui.__(
                            "householdInsurance.landingPage.benefitsInfo.upgrade.options.emergencyHomeAssistance.tooltip",
                        )}
                    />
                    <ListItemWithTooltip
                        symbol={<pr.IconSport />}
                        text={ui.__(
                            "householdInsurance.landingPage.benefitsInfo.upgrade.options.recreationPackage",
                        )}
                        tooltip={ui.__(
                            "householdInsurance.landingPage.benefitsInfo.upgrade.options.recreationPackage.tooltip",
                        )}
                    />
                    <ListItemWithTooltip
                        symbol={<pr.IconPet />}
                        text={ui.__(
                            "householdInsurance.landingPage.benefitsInfo.upgrade.options.animalLiability",
                        )}
                        tooltip={ui.__(
                            "householdInsurance.landingPage.benefitsInfo.upgrade.options.animalLiability.tooltip",
                        )}
                    />
                    <ListItemWithTooltip
                        maxWidth={150}
                        symbol={<pr.IconCreditCard />}
                        text={ui.__(
                            "householdInsurance.landingPage.benefitsInfo.upgrade.options.payProtection",
                        )}
                        tooltip={ui.__(
                            "householdInsurance.landingPage.benefitsInfo.upgrade.options.payProtection.tooltip",
                        )}
                    />
                    <ListItemWithTooltip
                        symbol={<pr.IconElectronics />}
                        text={ui.__(
                            "householdInsurance.landingPage.benefitsInfo.upgrade.options.electronics",
                        )}
                        tooltip={ui.__(
                            "householdInsurance.landingPage.benefitsInfo.upgrade.options.electronics.tooltip",
                        )}
                    />
                    <ListItemWithTooltip
                        symbol={<pr.IconHouseholdElectronics />}
                        text={ui.__(
                            "householdInsurance.landingPage.benefitsInfo.upgrade.options.householdElectronics",
                        )}
                        tooltip={ui.__(
                            "householdInsurance.landingPage.benefitsInfo.upgrade.options.householdElectronics.tooltip",
                        )}
                    />
                </InfoContainer>
            </SectionContainer>
            <pr.LightSeparatorLine style={{ marginTop: 50 }} />
        </>
    );
};



// WEBPACK FOOTER //
// ./src/components/householdInsurance/components/HoseholdInsuranceBenefitsInfo.tsx